import React, { useEffect } from "react";
import { API_BASE } from '../../config';
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  Stack,
  Text,
} from "@chakra-ui/react";
import { FiArrowRight, FiClock, FiMapPin, FiShield, FiUploadCloud } from "react-icons/fi";
import { useCookies } from "react-cookie";
import axios from "axios";
import Logo from "../UI/Images/pillstackLogo.png";
import StaffLoginForm from "../UI/StaffLoginForm";
import { AnimStyles, SocialProof } from "../UI/Primitives";
import { colors, shadows } from "../UI/designSystem";

const LOGIN_FEATURES = [
  {
    icon: FiUploadCloud,
    title: "Receive prescription uploads instantly",
    copy: "Review uploaded prescriptions and confirm medicines directly from the patient workflow.",
  },
  {
    icon: FiMapPin,
    title: "Manage your pharmacy profile",
    copy: "Update inventory, pricing, and delivery settings so patients can find your store.",
  },
  {
    icon: FiClock,
    title: "Track and fulfill orders",
    copy: "Accept, prepare, and dispatch orders with a clear view of every order stage.",
  },
];

export const Login = () => {
  const [cookies, setCookie, removeCookie] = useCookies(["token", "role"]);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname ?? null;
  const [checkingAuth, setCheckingAuth] = React.useState(true);

  const getDefaultRouteForRole = (role) => {
    if (role === "pharmacist") return "/pharmacist/home";
    if (role === "admin") return "/admin-home";
    return "/home";
  };

  useEffect(() => {
    let mounted = true;
    const verify = async () => {
      if (!cookies.token) {
        setCheckingAuth(false);
        return;
      }
      try {
        const { data } = await axios.post(
          API_BASE,
          {},
          { withCredentials: true },
        );
        if (data?.status && mounted) {
          if (data?.role) {
            setCookie("role", data.role, { path: "/" });
          }
          // Only auto-redirect non-patient roles; patients can use /login to switch accounts
          if (data?.role && data.role !== "patient") {
            navigate(redirectTo || getDefaultRouteForRole(data?.role), { replace: true });
            return;
          }
          setCheckingAuth(false);
          return;
        }
        removeCookie("token", { path: "/" });
        removeCookie("role", { path: "/" });
      } catch (err) {
        removeCookie("token", { path: "/" });
        removeCookie("role", { path: "/" });
      } finally {
        if (mounted) setCheckingAuth(false);
      }
    };
    verify();
    return () => {
      mounted = false;
    };
  }, [cookies.token, navigate, redirectTo, removeCookie, setCookie]);

  if (checkingAuth && cookies.token) {
    return null;
  }

  return (
    <Box minH="100vh" bg={colors.neutral[50]} color={colors.neutral[900]}>
      <AnimStyles />
      <Grid templateColumns={{ base: "1fr", xl: "1.02fr 0.98fr" }} minH="100vh">
        <GridItem
          px={{ base: 5, md: 8, xl: 12 }}
          py={{ base: 8, md: 10 }}
          position="relative"
          overflow="hidden"
          bg={`linear-gradient(180deg, ${colors.neutral[0]} 0%, ${colors.brand[50]} 100%)`}
        >
          <Box
            position="absolute"
            top="-120px"
            left="-140px"
            w="360px"
            h="360px"
            borderRadius="full"
            bg={`radial-gradient(circle, ${colors.brand[100]} 0%, transparent 68%)`}
            pointerEvents="none"
          />
          <Flex direction="column" justify="space-between" h="100%" position="relative">
            <Box maxW="620px">
              <Image src={Logo} alt="PillStack" h={{ base: "44px", md: "52px" }} />
              <HStack
                mt={8}
                spacing={2}
                display="inline-flex"
                px={3}
                py="7px"
                borderRadius="full"
                bg="white"
                border={`1px solid ${colors.brand[100]}`}
                boxShadow={shadows.xs}
              >
                <FiShield color={colors.brand[700]} />
                <Text fontSize="12px" fontWeight="800" color={colors.brand[700]} m={0}>
                  Staff access
                </Text>
              </HStack>

              <Heading
                mt={5}
                fontSize={{ base: "40px", md: "56px" }}
                lineHeight="0.98"
                letterSpacing="-0.05em"
              >
                Manage your pharmacy workspace.
              </Heading>

              <Text mt={5} fontSize={{ base: "16px", md: "18px" }} color={colors.neutral[600]} lineHeight="1.8" maxW="560px">
                Pharmacists and admins use this portal to manage inventory, prescriptions, and orders. Patients should use the Sign In button on the home page.
              </Text>

              <Stack spacing={4} mt={8}>
                {LOGIN_FEATURES.map((item) => (
                  <Flex
                    key={item.title}
                    gap={4}
                    align="flex-start"
                    p={4}
                    bg="rgba(255,255,255,0.74)"
                    border={`1px solid ${colors.neutral[200]}`}
                    borderRadius="22px"
                    boxShadow={shadows.xs}
                    backdropFilter="blur(8px)"
                  >
                    <Box
                      w="48px"
                      h="48px"
                      borderRadius="16px"
                      bg={colors.brand[50]}
                      color={colors.brand[700]}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      <item.icon size={22} />
                    </Box>
                    <Box>
                      <Text fontSize="16px" fontWeight="800" color={colors.neutral[900]} m={0}>
                        {item.title}
                      </Text>
                      <Text fontSize="14px" color={colors.neutral[600]} mt={1.5} lineHeight="1.75" m={0}>
                        {item.copy}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </Stack>
            </Box>

            <Box mt={{ base: 10, xl: 14 }}>
              <HStack spacing={5} flexWrap="wrap">
                <SocialProof orderCount="50,000+" />
                <HStack spacing={1.5}>
                  <FiClock color={colors.brand[600]} />
                  <Text fontSize="13px" fontWeight="700" color={colors.neutral[600]} m={0}>
                    Delivery-ready workflow
                  </Text>
                </HStack>
                <HStack spacing={1.5}>
                  <FiMapPin color={colors.brand[600]} />
                  <Text fontSize="13px" fontWeight="700" color={colors.neutral[600]} m={0}>
                    Local pharmacy selection
                  </Text>
                </HStack>
              </HStack>
            </Box>
          </Flex>
        </GridItem>

        <GridItem
          bg={`linear-gradient(145deg, ${colors.neutral[900]} 0%, ${colors.brand[800]} 100%)`}
          px={{ base: 5, md: 8, xl: 10 }}
          py={{ base: 8, md: 10 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            w="100%"
            maxW="520px"
            bg="rgba(255,255,255,0.96)"
            borderRadius="32px"
            px={{ base: 5, md: 7 }}
            py={{ base: 6, md: 7 }}
            boxShadow="0 28px 80px rgba(3, 10, 18, 0.28)"
            border="1px solid rgba(255,255,255,0.24)"
          >
            <Stack spacing={2} textAlign="left">
              <Text fontSize="12px" fontWeight="800" color={colors.brand[600]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
                Staff access
              </Text>
              <Heading fontSize={{ base: "28px", md: "34px" }} lineHeight="1.08" letterSpacing="-0.04em">
                Pharmacist / Admin sign in
              </Heading>
              <Text fontSize="15px" color={colors.neutral[600]} lineHeight="1.7" maxW="420px">
                Enter your username and password to continue.
              </Text>
            </Stack>

            <Box mt={6}>
              <StaffLoginForm redirectPath={redirectTo} showLinks={false} />
            </Box>

            <Flex mt={6} justify="space-between" gap={4} direction={{ base: "column", md: "row" }}>
              <Button
                variant="ghost"
                justifyContent="flex-start"
                rightIcon={<FiArrowRight />}
                color={colors.neutral[600]}
                onClick={() => navigate("/")}
              >
                Back to home
              </Button>
              <Button
                variant="outline"
                borderColor={colors.brand[200]}
                color={colors.brand[700]}
                onClick={() => navigate("/pharmacist-register")}
              >
                Pharmacist onboarding
              </Button>
            </Flex>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};
